import * as React from 'react';
import { useNotify } from 'react-admin';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button as MuiButton } from '@material-ui/core';

/**
 * For edits to records which belong to an employment's deal (allowances,
 * hiatuses, penalty rate overrides, contract notes).
 *
 * The API refuses a change to a deal which is pending approval, and asks for
 * confirmation when the change will cancel a contract which has been sent for
 * signature. This hook deals with both so each panel does not have to:
 *
 *   const { run, guardDialog } = useDealGuard();
 *
 *   run(
 *       (extra) => dataProvider.update(resource, { id, data: { ...payload, ...extra } }),
 *       { onSuccess: () => {...}, onError: (err) => {...} }
 *   );
 *
 *   return <>...{guardDialog}</>;
 *
 * `request` gets the confirmation flags to merge into the request data (empty
 * the first time). `onError` is called for anything which is not a deal
 * message, e.g. validation errors, so the panel can show its own.
 */
export const useDealGuard = () => {
    const notify = useNotify();
    const [pending, setPending] = React.useState(null);
    const [busy, setBusy] = React.useState(false);

    const run = React.useCallback((request, { onSuccess, onError } = {}) => {
        const attempt = (extra, confirmed) =>
            request(extra)
                .then(result => {
                    setPending(null);
                    setBusy(false);
                    if (onSuccess) onSuccess(result);
                })
                .catch(err => {
                    const warning = err?.body?.warnings?.contract_will_be_cancelled;

                    if (warning && !confirmed) {
                        setBusy(false);
                        setPending({
                            message: warning.message,
                            confirm: () => {
                                setBusy(true);
                                attempt({ confirm_contract_cancel: true }, true);
                            },
                        });
                        return;
                    }

                    setPending(null);
                    setBusy(false);

                    if (warning) {
                        // Asked again after confirming, so the flag did not reach the API
                        notify('The contract cancellation could not be confirmed. Nothing was changed.', 'error');
                        return;
                    }

                    // A plain message, e.g. the deal is pending approval and has to be withdrawn first
                    if (err?.body?.message && !err.body.errors && Object.keys(err.body).length <= 1) {
                        notify(err.body.message, 'error');
                        return;
                    }

                    if (onError) {
                        onError(err);
                    } else {
                        notify(err?.message ?? 'Something went wrong', 'error');
                    }
                });

        attempt({}, false);
    }, [notify]);

    const guardDialog = (
        <Dialog open={!!pending} onClose={() => !busy && setPending(null)} maxWidth="sm" fullWidth>
            <DialogTitle>This will cancel the contract</DialogTitle>
            <DialogContent>
                <Typography variant="body1" gutterBottom>
                    {pending?.message}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    The deal will go back to draft. Send it for approval again, and once the
                    crew member has accepted it a new contract can be sent.
                </Typography>
            </DialogContent>
            <DialogActions>
                <MuiButton onClick={() => setPending(null)} disabled={busy}>Cancel — go back</MuiButton>
                <MuiButton onClick={() => pending?.confirm()} color="primary" variant="contained" disabled={busy}>
                    Cancel contract and save
                </MuiButton>
            </DialogActions>
        </Dialog>
    );

    return { run, guardDialog };
};
